import axios from 'axios';
import { AuthResponse, LoginCredentials, RegisterData, User, Post, CreatePostData, CategoryInfo, PostCategory, Contact, CreateContactData, Document, UploadDocumentData, DocumentCategory, DocumentCategoryInfo, Comment, CreateCommentData } from './types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors - but not on login page (allows showing lockout messages)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Don't redirect if we're on the login page - let the error show
            const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';
            const isLoginRequest = error.config?.url?.includes('/auth/login');
            
            if (!isLoginPage && !isLoginRequest) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authApi = {
    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>('/auth/login', credentials);
        return response.data;
    },

    register: async (data: RegisterData): Promise<{ message: string; email: string }> => {
        const response = await api.post('/auth/register', data);
        return response.data;
    },

    getCurrentUser: async (): Promise<User> => {
        const response = await api.get<User>('/auth/me');
        return response.data;
    },

    getAllUsers: async (): Promise<User[]> => {
        const response = await api.get<User[]>('/auth/users');
        return response.data;
    },

    updateUserRole: async (userId: string, role: string): Promise<void> => {
        await api.put(`/auth/users/${userId}/role`, JSON.stringify(role), {
            headers: { 'Content-Type': 'application/json' }
        });
    },

    unlockUser: async (userId: string): Promise<void> => {
        await api.post(`/auth/users/${userId}/unlock`);
    },

    lockUser: async (userId: string): Promise<void> => {
        await api.post(`/auth/users/${userId}/lock`);
    },

    deleteUser: async (userId: string): Promise<void> => {
        await api.delete(`/auth/users/${userId}`);
    },
};

// Posts API
export const postsApi = {
    getPosts: async (category?: PostCategory): Promise<Post[]> => {
        const params = category !== undefined ? { category } : {};
        const response = await api.get<Post[]>('/posts', { params });
        return response.data;
    },

    getPost: async (id: number): Promise<Post> => {
        const response = await api.get<Post>(`/posts/${id}`);
        return response.data;
    },

    createPost: async (data: CreatePostData): Promise<Post> => {
        const response = await api.post<Post>('/posts', data);
        return response.data;
    },

    updatePost: async (id: number, data: Partial<CreatePostData>): Promise<Post> => {
        const response = await api.put<Post>(`/posts/${id}`, data);
        return response.data;
    },

    deletePost: async (id: number): Promise<void> => {
        await api.delete(`/posts/${id}`);
    },

    getCategories: async (): Promise<CategoryInfo[]> => {
        const response = await api.get<CategoryInfo[]>('/posts/categories');
        return response.data;
    },
};

// Comments API
export const commentsApi = {
    getComments: async (postId: number): Promise<Comment[]> => {
        const response = await api.get<Comment[]>(`/posts/${postId}/comments`);
        return response.data;
    },

    createComment: async (postId: number, data: CreateCommentData): Promise<Comment> => {
        const response = await api.post<Comment>(`/posts/${postId}/comments`, data);
        return response.data;
    },

    updateComment: async (postId: number, commentId: number, data: CreateCommentData): Promise<Comment> => {
        const response = await api.put<Comment>(`/posts/${postId}/comments/${commentId}`, data);
        return response.data;
    },

    deleteComment: async (postId: number, commentId: number): Promise<void> => {
        await api.delete(`/posts/${postId}/comments/${commentId}`);
    },
};

// Contacts API
export const contactsApi = {
    getContacts: async (): Promise<Contact[]> => {
        const response = await api.get<Contact[]>('/contacts');
        return response.data;
    },

    getContact: async (id: number): Promise<Contact> => {
        const response = await api.get<Contact>(`/contacts/${id}`);
        return response.data;
    },

    createContact: async (data: CreateContactData): Promise<Contact> => {
        const response = await api.post<Contact>('/contacts', data);
        return response.data;
    },

    updateContact: async (id: number, data: Partial<CreateContactData>): Promise<Contact> => {
        const response = await api.put<Contact>(`/contacts/${id}`, data);
        return response.data;
    },

    deleteContact: async (id: number): Promise<void> => {
        await api.delete(`/contacts/${id}`);
    },
};

// Documents API
export const documentsApi = {
    getDocuments: async (category?: DocumentCategory): Promise<Document[]> => {
        const params = category !== undefined ? { category } : {};
        const response = await api.get<Document[]>('/documents', { params });
        return response.data;
    },

    getDocument: async (id: number): Promise<Document> => {
        const response = await api.get<Document>(`/documents/${id}`);
        return response.data;
    },

    uploadDocument: async (data: UploadDocumentData, file: File): Promise<Document> => {
        const formData = new FormData();
        formData.append('title', data.title);
        if (data.description) {
            formData.append('description', data.description);
        }
        formData.append('category', data.category.toString());
        formData.append('file', file);

        const response = await api.post<Document>('/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    updateDocument: async (id: number, data: Partial<UploadDocumentData>): Promise<Document> => {
        const response = await api.put<Document>(`/documents/${id}`, data);
        return response.data;
    },

    deleteDocument: async (id: number): Promise<void> => {
        await api.delete(`/documents/${id}`);
    },

    getCategories: async (): Promise<DocumentCategoryInfo[]> => {
        const response = await api.get<DocumentCategoryInfo[]>('/documents/categories');
        return response.data;
    },

    getDownloadUrl: (id: number): string => {
        return `${API_BASE_URL}/documents/${id}/download`;
    },
};

export default api;
